/**
 * useUndoRedo Hook
 *
 * Manages the undo/redo history for the map editor.
 * Tracks actions performed on regions and enables reverting or re-applying them.
 *
 * Features:
 * - Track action history (add, delete, update regions)
 * - Undo last action
 * - Redo previously undone action
 * - Clear history
 *
 * @example
 * ```tsx
 * const { canUndo, canRedo, pushAction, undo, redo, clearHistory } = useUndoRedo(regions);
 *
 * // After adding a region
 * pushAction({ type: 'ADD_REGION', regionName: 'New Wall', newPolygon: [...] });
 *
 * // Undo the action
 * const updatedRegions = undo(currentRegions);
 * if (updatedRegions) {
 *   setRegions(updatedRegions);
 * }
 * ```
 */

import { useState, useCallback, useMemo } from 'react';
import type { Point, WallSectionRegion } from '@flash/shared';

/**
 * Types of editor actions that can be undone/redone
 */
export type EditorActionType = 'ADD_REGION' | 'DELETE_REGION' | 'UPDATE_REGION';

/**
 * Action for adding a new region
 */
export interface AddRegionAction {
  type: 'ADD_REGION';
  regionName: string;
  newPolygon: Point[];
}

/**
 * Action for deleting a region
 */
export interface DeleteRegionAction {
  type: 'DELETE_REGION';
  regionName: string;
  previousPolygon: Point[];
}

/**
 * Action for updating a region's polygon
 */
export interface UpdateRegionAction {
  type: 'UPDATE_REGION';
  regionName: string;
  previousPolygon: Point[];
  newPolygon: Point[];
}

/**
 * Union type for all editor actions
 */
export type EditorAction = AddRegionAction | DeleteRegionAction | UpdateRegionAction;

/**
 * Return value from useUndoRedo hook
 */
export interface UseUndoRedoResult {
  /** Whether there are actions that can be undone */
  canUndo: boolean;

  /** Whether there are actions that can be redone */
  canRedo: boolean;

  /** Push a new action to the history */
  pushAction: (action: EditorAction) => void;

  /** Undo the last action, returns new regions or null if nothing to undo */
  undo: (currentRegions: WallSectionRegion[]) => WallSectionRegion[] | null;

  /** Redo the last undone action, returns new regions or null if nothing to redo */
  redo: (currentRegions: WallSectionRegion[]) => WallSectionRegion[] | null;

  /** Clear all undo/redo history */
  clearHistory: () => void;
}

/**
 * Apply the reverse of an action to produce undo effect
 */
const applyUndoAction = (
  action: EditorAction,
  currentRegions: WallSectionRegion[]
): WallSectionRegion[] => {
  switch (action.type) {
    case 'ADD_REGION':
      // Undo add = remove the region
      return currentRegions.filter((r) => r.wallSectionName !== action.regionName);

    case 'DELETE_REGION':
      // Undo delete = restore the region
      return [
        ...currentRegions,
        { wallSectionName: action.regionName, polygon: action.previousPolygon },
      ];

    case 'UPDATE_REGION':
      // Undo update = restore previous polygon
      return currentRegions.map((r) =>
        r.wallSectionName === action.regionName ? { ...r, polygon: action.previousPolygon } : r
      );

    default:
      return currentRegions;
  }
};

/**
 * Apply an action to produce redo effect
 */
const applyRedoAction = (
  action: EditorAction,
  currentRegions: WallSectionRegion[]
): WallSectionRegion[] => {
  switch (action.type) {
    case 'ADD_REGION':
      // Redo add = add the region back
      return [
        ...currentRegions,
        { wallSectionName: action.regionName, polygon: action.newPolygon },
      ];

    case 'DELETE_REGION':
      // Redo delete = remove the region again
      return currentRegions.filter((r) => r.wallSectionName !== action.regionName);

    case 'UPDATE_REGION':
      // Redo update = apply new polygon
      return currentRegions.map((r) =>
        r.wallSectionName === action.regionName ? { ...r, polygon: action.newPolygon } : r
      );

    default:
      return currentRegions;
  }
};

/**
 * Hook for managing undo/redo history
 *
 * @param _initialRegions - Initial regions (not used, but included for API consistency)
 * @returns Undo/redo state and control functions
 */
export const useUndoRedo = (_initialRegions: WallSectionRegion[]): UseUndoRedoResult => {
  // Stack of actions that can be undone
  const [undoStack, setUndoStack] = useState<EditorAction[]>([]);

  // Stack of actions that can be redone
  const [redoStack, setRedoStack] = useState<EditorAction[]>([]);

  /**
   * Whether there are actions that can be undone
   */
  const canUndo = useMemo(() => undoStack.length > 0, [undoStack.length]);

  /**
   * Whether there are actions that can be redone
   */
  const canRedo = useMemo(() => redoStack.length > 0, [redoStack.length]);

  /**
   * Push a new action to the undo stack
   * Clears the redo stack (can't redo after a new action)
   */
  const pushAction = useCallback((action: EditorAction) => {
    setUndoStack((prev) => [...prev, action]);
    setRedoStack([]); // Clear redo stack on new action
  }, []);

  /**
   * Undo the last action
   * Returns the new regions array, or null if nothing to undo
   */
  const undo = useCallback(
    (currentRegions: WallSectionRegion[]): WallSectionRegion[] | null => {
      if (undoStack.length === 0) return null;

      // Get the last action
      const action = undoStack[undoStack.length - 1]!;

      // Move it to redo stack
      setUndoStack((prev) => prev.slice(0, -1));
      setRedoStack((prev) => [...prev, action]);

      // Apply the reverse of the action
      return applyUndoAction(action, currentRegions);
    },
    [undoStack]
  );

  /**
   * Redo the last undone action
   * Returns the new regions array, or null if nothing to redo
   */
  const redo = useCallback(
    (currentRegions: WallSectionRegion[]): WallSectionRegion[] | null => {
      if (redoStack.length === 0) return null;

      // Get the last undone action
      const action = redoStack[redoStack.length - 1]!;

      // Move it back to undo stack
      setRedoStack((prev) => prev.slice(0, -1));
      setUndoStack((prev) => [...prev, action]);

      // Apply the action
      return applyRedoAction(action, currentRegions);
    },
    [redoStack]
  );

  /**
   * Clear all undo/redo history
   */
  const clearHistory = useCallback(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  return {
    canUndo,
    canRedo,
    pushAction,
    undo,
    redo,
    clearHistory,
  };
};

export default useUndoRedo;
